import { infiniteWarCatalog } from "../../../data/infinite-war-catalog";
import type { IwAimMode, IwCardId, IwRoleId, IwTeam } from "./types";

export const U = infiniteWarCatalog.units;
export const C = infiniteWarCatalog.cards;

export function roleStats(roleId: IwRoleId) {
  return U.roles[roleId];
}

/** Summonable role `tier` (demigods omit). */
export function roleTier(roleId: IwRoleId): number | null {
  const role = roleStats(roleId) as { tier?: number; tags: readonly string[] };
  if (role.tags.includes("demigod")) return null;
  return typeof role.tier === "number" ? role.tier : null;
}

/**
 * Cross-faction rebirth target: same `tier` on killer team.
 * Throws if victim has no tier or killer roster lacks a match.
 */
export function matchedRoleFor(victimRoleId: IwRoleId, killerTeam: IwTeam): IwRoleId {
  const tier = roleTier(victimRoleId);
  if (tier == null) {
    throw new Error(`Infinite War: role ${victimRoleId} has no rebirth tier`);
  }
  for (const id of Object.keys(U.roles) as IwRoleId[]) {
    const role = roleStats(id) as { team: IwTeam; tier?: number; tags: readonly string[] };
    if (role.team !== killerTeam) continue;
    if (role.tags.includes("demigod")) continue;
    if (role.tier === tier) return id;
  }
  throw new Error(`Infinite War: no tier ${tier} role for team ${killerTeam}`);
}

export function cardDef(card: IwCardId) {
  return C.cards[card];
}

export function cardCost(card: IwCardId): number {
  return C.cards[card].cost;
}

export function cardAim(card: IwCardId): IwAimMode {
  const def = C.cards[card];
  if ("aim" in def && def.aim) {
    const aim = def.aim as string;
    if (aim === "direction" || aim === "position" || aim === "confirm") {
      return aim;
    }
  }
  if (def.kind === "summon") return "position";
  if (def.kind === "shockwave" || def.kind === "cone") return "direction";
  if (def.kind === "board_half") return "confirm";
  return "position";
}

/** String key for aim-phase hint (glue resolves via `t()`). */
export function cardAimHintKey(card: IwCardId): string {
  const aim = cardAim(card);
  if (aim === "direction") return "iw.hint.aim";
  if (aim === "confirm") return "iw.hint.confirm";
  if (C.cards[card].kind === "summon") return "iw.hint.aimSummon";
  return "iw.hint.aimPos";
}

/** True when playCard needs board x/y for this aim mode. */
export function cardNeedsTarget(aim: IwAimMode): boolean {
  return aim === "position" || aim === "direction";
}

export function aoeDamageMul(roleId: IwRoleId): number {
  const mul = (roleStats(roleId) as { aoeDamageMul?: number }).aoeDamageMul;
  return mul == null ? 1 : mul;
}
